import React from 'react';

/**
 * Closes a cell's editor when the grid or page scrolls, since the window-anchored editor
 * would float away from its cell. Capture phase because scroll events do not bubble.
 */
export const CloseOnScroll = ({
  close,
  panel
}: {
  close: () => void;
  panel: React.RefObject<HTMLElement | null>;
}) => {
  const scrollingIntoView = React.useContext(ScrollingIntoView);
  React.useEffect(() => {
    // The editor's own option list scrolling is not the grid moving away from it.
    const inPanel = (event: Event) =>
      event.target instanceof Node && Boolean(panel.current?.contains(event.target));
    // A wheel or swipe is always the user, even mid auto-scroll.
    const byUser = (event: Event) => {
      if (!inPanel(event)) {
        close();
      }
    };
    const onScroll = (event: Event) => {
      if (!inPanel(event) && !scrollingIntoView.current) {
        close();
      }
    };
    const options = { capture: true, passive: true };
    window.addEventListener('wheel', byUser, options);
    window.addEventListener('touchmove', byUser, options);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('wheel', byUser, options);
      window.removeEventListener('touchmove', byUser, options);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [close, panel, scrollingIntoView]);
  return null;
};

/** Scroll margin counts, so a cell under the grid's sticky header is not visible. */
const fullyInView = (element: HTMLElement): boolean => {
  const rect = element.getBoundingClientRect();
  const marginTop = parseFloat(getComputedStyle(element).scrollMarginTop) || 0;
  let top = 0;
  let left = 0;
  let bottom = window.innerHeight;
  let right = window.innerWidth;
  for (let box = element.parentElement; box; box = box.parentElement) {
    const style = getComputedStyle(box);
    if (/auto|scroll|hidden/.test(`${style.overflowX} ${style.overflowY}`)) {
      const bounds = box.getBoundingClientRect();
      top = Math.max(top, bounds.top);
      left = Math.max(left, bounds.left);
      bottom = Math.min(bottom, bounds.bottom);
      right = Math.min(right, bounds.right);
    }
  }
  return (
    rect.top - marginTop >= top && rect.left >= left && rect.bottom <= bottom && rect.right <= right
  );
};

/** How long scrolling must pause before the grid's own scroll counts as finished. */
const SCROLL_SETTLED_MS = 150;

/**
 * Brings a partly hidden cell fully into view once its editor has opened. Waits two
 * frames because a smooth scroll started as the editor mounts gets cancelled by the
 * opening's own scrolling. Scroll events are ignored until scrolling pauses, since the
 * grid and the window each finish on their own.
 */
export const RevealOnOpen = ({ cell }: { cell: React.RefObject<HTMLElement | null> }) => {
  const scrollingIntoView = React.useContext(ScrollingIntoView);
  React.useEffect(() => {
    let settled: ReturnType<typeof setTimeout> | undefined;
    const finish = () => {
      clearTimeout(settled);
      window.removeEventListener('scroll', waitForPause, true);
      scrollingIntoView.current = false;
    };
    const waitForPause = () => {
      clearTimeout(settled);
      settled = setTimeout(finish, SCROLL_SETTLED_MS);
    };
    let frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(() => {
        const target = cell.current;
        if (!target || fullyInView(target)) {
          return;
        }
        scrollingIntoView.current = true;
        window.addEventListener('scroll', waitForPause, true);
        // Covers a scroll that never starts, which fires no event to wait on.
        waitForPause();
        // `?.`: jsdom has no matchMedia.
        const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        target.scrollIntoView({
          block: 'nearest',
          inline: 'nearest',
          behavior: reduceMotion ? 'instant' : 'smooth'
        });
      });
    });
    return () => {
      cancelAnimationFrame(frame);
      finish();
    };
  }, [cell, scrollingIntoView]);
  return null;
};

/** Whether a scroll now under way is the grid bringing a cell into view. */
export const ScrollingIntoView = React.createContext<{ current: boolean }>({ current: false });
